import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../shared/i18n';
import { userPath } from '../../shared/config/paths';
import { formatMoney } from '../../shared/utils/money';
import couponService from '../services/couponService';
import { setPendingCoupon, withCoupon } from '../utils/pendingCoupon';
import './couponTicket.css';
import { CampaignMotif, themeVars } from '../../shared/campaign/campaignThemes';
import TicketValue from '../../shared/campaign/TicketValue';

const TRIAL_PERIOD = { monthly: '1 MONTH FREE', quarterly: '1 QUARTER FREE' };

/** Big stub value: "10% OFF" / "$5 OFF" / "1 MONTH FREE". */
export const ticketValue = (c, t) => {
    if (c.type === 'trial') return t(TRIAL_PERIOD[c.trial_cycle] || '1 MONTH FREE');
    if (c.type === 'percent') return t('{{value}}% OFF', { value: Number(c.value) });
    return t('{{value}} OFF', { value: formatMoney(c.value, c.currency) });
};

/** Where "Use now" goes: the coupon's purchase page with ?coupon= (also kept for the cart). */
export const couponUsePath = (c) => withCoupon(c.use_link || '/dashboard', c.code);

/** Campaign theme of a ticket (wallet row: c.campaign.theme; ticket / banner code: c.theme). */
export const ticketTheme = (c) => (c.theme || c.campaign?.theme || null);

/** One coupon ticket: stub with the value, perforation, scope / expiry / code. Campaign coupons get the holiday theme. */
export const CouponTicket = ({ c, onClick }) => {
    const { t } = useTranslation();
    const theme = ticketTheme(c);
    const campaign = c.campaign_name || c.campaign?.name;
    const click = onClick ? {
        role: 'button', tabIndex: 0, title: t('Use now'), onClick,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
    } : { role: 'group' };
    return (
        <div className={`cpt${theme ? ` cpt--themed cpt--${theme}` : ''}${onClick ? ' cpt--click' : ''}`} aria-label={c.name} style={theme ? themeVars(theme) : undefined} {...click}>
            <div className="cpt-stub">
                {theme ? <CampaignMotif theme={theme} size={30} className="cpt-motif" /> : null}
                {theme && campaign ? <span className="cpt-campaign">{campaign}</span> : null}
                <TicketValue c={c} t={t} />
            </div>
            <div className="cpt-body">
                <div className="cpt-name">{c.name}</div>
                <div className="cpt-scope">
                    {(c.scope_type || c.applies_to || c.scope?.type || c.scope?.products?.length) && c.scope_text ? c.scope_text : t('All products')}{c.max_items ? ` · ${t('Up to {{n}} items', { n: c.max_items })}` : ''}
                </div>
                {c.expires_at && <div className="cpt-exp">{t('Valid until {{date}}', { date: dayjs(c.expires_at).format('YYYY-MM-DD') })}</div>}
                <span className="cpt-code">{c.code}</span>
            </div>
        </div>
    );
};

/** Several tickets: one at a time with a small pager. */
export const TicketPager = ({ coupons, index, onIndex }) => {
    const { t } = useTranslation();
    if (!coupons.length) return null;
    const c = coupons[Math.min(index, coupons.length - 1)];
    return (
        <div className="cpt-wrap">
            <CouponTicket key={c.user_coupon_id} c={c} />
            {coupons.length > 1 && (
                <div className="cpt-pager">
                    <Button size="small" type="text" icon={<LeftOutlined />} disabled={index === 0} onClick={() => onIndex(index - 1)} aria-label={t('Previous')} />
                    <span>{index + 1} / {coupons.length}</span>
                    <Button size="small" type="text" icon={<RightOutlined />} disabled={index >= coupons.length - 1} onClick={() => onIndex(index + 1)} aria-label={t('Next')} />
                </div>
            )}
        </div>
    );
};

/** Several tickets side by side (wraps into rows); clicking one uses it. */
export const TicketGrid = ({ coupons, onUse }) => (
    <div className={`cpt-grid${coupons.length > 1 ? ' cpt-grid--multi' : ''}`}>
        {coupons.map((c) => <CouponTicket key={c.user_coupon_id} c={c} onClick={() => onUse(c)} />)}
    </div>
);

/** Load unseen coupons (when enabled) + mark them all seen once. */
export const useUnseenCoupons = (enabled) => {
    const [coupons, setCoupons] = useState([]);
    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        couponService.unseen().then((r) => { if (alive && r?.success) setCoupons(r.data || []); });
        return () => { alive = false; };
    }, [enabled]);
    const markAllSeen = () => {
        const ids = coupons.map((c) => c.user_coupon_id);
        setCoupons([]);
        if (ids.length) couponService.markSeen(ids);
    };
    return [coupons, markAllSeen];
};

/**
 * New coupons in the wallet, shown once as tickets side by side (layout level).
 * Click a ticket → its purchase page with the code; "Later" / close → seen. The coupons stay in My coupons.
 */
const CouponTicketModal = ({ enabled }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [coupons, markAllSeen] = useUnseenCoupons(enabled);
    const use = (c) => {
        setPendingCoupon(c.code);
        markAllSeen();
        navigate(userPath(couponUsePath(c)));
    };
    return (
        <Modal open={coupons.length > 0} onCancel={markAllSeen} footer={null} centered width={coupons.length > 1 ? 640 : 480}
            title={coupons.length > 1 ? t('You have {{n}} new coupons', { n: coupons.length }) : t('You have a new coupon')} className="cpt-modal">
            {coupons.length > 0 && (
                <>
                    <TicketGrid coupons={coupons} onUse={use} />
                    <div className="cpt-actions">
                        <Button onClick={markAllSeen}>{t('Later')}</Button>
                        {coupons.length === 1 && <Button type="primary" onClick={() => use(coupons[0])}>{t('Use now')}</Button>}
                    </div>
                </>
            )}
        </Modal>
    );
};

export default CouponTicketModal;
